// 启动加载屏（老板 2026-09-15：打开游戏加 loading 动画）。
// 进图条期间只预载首屏所需：音乐 + 房间/庭院背景 + 小狗；其余礼物/明信片由浏览器按需加载（美术圣经 §9 加载策略）。
// 进度满后自动进入（老板 2026-09-16 反馈）。
// ⚠️ 自动进入是 rAF 回调，**不是**真实用户手势；其中 play() 会被浏览器自动播放策略拦截。
// 音乐改由 App 注册的「首个真手势」监听启动（见 audio/music.ts 的 bindFirstGestureToPlay）。
// B1 守门（away/reveal）不覆盖本组件，百分比走 aria 属性而非 DOM 文案。
import { useEffect, useState, useRef } from 'react';
import { assetIndex } from '../assets/assetIndex';
import { prepareAudio } from '../audio/music';

interface BootScreenProps {
  onEnter: () => void;
}

// 首屏锚图：房间背景 + 庭院背景 + 小狗（礼物/明信片按需加载），逻辑路径来自 assetIndex（单一来源）。
// 庭院与房间在 home 态随机切换（SceneView v2.2），故两者都须预载，避免切到庭院时闪灰。
// 仅预载 3 张 ≈ 0.52 MB（WebP 后实测 225684 + 234656 + 63706 = 524046 B；原 Object.values 全量 25 张 PNG ≈ 31.9 MB）。
const ASSET_URLS = [
  assetIndex.room_day!,
  assetIndex.scene_yard_day!,
  assetIndex.dog_idle!,
]; // 三键均存在 manifest（room_day / scene_yard_day / dog_idle），见 art/manifest.json
const TOTAL = ASSET_URLS.length + 1; // +1 为音乐预载

export function BootScreen({ onEnter }: BootScreenProps) {
  const [loaded, setLoaded] = useState(0);
  const ready = loaded >= TOTAL;
  const enteredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let count = 0;
    const bump = () => {
      count += 1;
      if (cancelled) return;
      setLoaded(count);
      // 进度满后：等一帧渲染出 100%，再触发 onEnter（音乐自动播放手势）
      if (count >= TOTAL && !enteredRef.current) {
        enteredRef.current = true;
        // 短暂延迟确保 100% 进度条被渲染出来，提升视觉完成感
        requestAnimationFrame(() => requestAnimationFrame(() => { onEnter(); }));
      }
    };

    // 音乐：仅缓冲不播放
    try {
      prepareAudio();
    } catch {
      /* 忽略 */
    }
    bump(); // 音乐预载已发起，计入进度

    // 锚图：用 Image 预载（onload/onerror 都计入，避免单张失败卡死进度条）
    for (const url of ASSET_URLS) {
      const img = new Image();
      img.onload = bump;
      img.onerror = bump;
      img.src = url;
    }

    return () => {
      cancelled = true;
    };
  }, [onEnter, TOTAL]);

  const pct = Math.min(100, Math.round((loaded / TOTAL) * 100));

  return (
    <div className="boot-screen">
      <div className="boot-screen__card">
        <img className="boot-screen__dog" src={assetIndex.dog_idle} alt="" aria-hidden="true" />
        <p className="boot-screen__title">小狗旅行</p>
        <div
          className="boot-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label="资源加载进度"
        >
          <div className="boot-bar__fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="boot-screen__hint">
          {ready ? '即将进入…' : '正在准备房间…'}
        </p>
      </div>
    </div>
  );
}
